import { Component, OnInit, AfterViewInit, OnDestroy, HostListener, ViewChild, ElementRef } from '@angular/core';
import { CommonModule, DatePipe, DecimalPipe } from '@angular/common';
import { DashboardService } from '../../../services/dashboard.service';
import { CardModule } from 'primeng/card';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { TopAaccountingRecodsLookup } from '../../Lookup/top-aaccounting-recods-lookup/top-aaccounting-recods-lookup';
import Chart from 'chart.js/auto';
import { FormsModule } from '@angular/forms';
import { CountUp } from 'countup.js';

interface MonthlyTableRow {
  month: string;
  incoming: number;
  outgoing: number;
  net: number;
  inPct: number;
  outPct: number;
}

@Component({
  selector: 'app-accounting-dashboard',
  standalone: true,
  imports: [CommonModule, CardModule, FormsModule],
  templateUrl: './accounting-dashboard.component.html',
  styleUrls: ['./accounting-dashboard.component.scss'],
  providers: [DatePipe, DecimalPipe]
})
export class AccountingDashboardComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('cashflowChart') cashflowCanvas!: ElementRef<HTMLCanvasElement>;

  receivables: any;
  payables: any;

  receivablePercent = 0;
  payablePercent = 0;
  netPosition = 0;
  netPercent = 0;

  openingCash = 0;
  totalIncoming = 0;
  totalOutgoing = 0;
  closingCash = 0;

  showOverduePopup = false;
  showPayablePopup = false;
  isDialogOpen = false;
  todayDate = '';

  periods = ['This Fiscal Year', 'Previous Fiscal Year', 'Last 12 Months'];
  selectedPeriod = 'This Fiscal Year';
  monthlyTable: MonthlyTableRow[] = [];
  private cashflowChartInstance: Chart | null = null;
  ref: DynamicDialogRef | null = null;

  constructor(
    private dashboardService: DashboardService,
    public dialogService: DialogService,
    private datePipe: DatePipe
  ) { }


  ngOnInit(): void {
    document.title = 'Accounting Dashboard';
    this.todayDate = this.datePipe.transform(new Date(), 'dd MMM yyyy') ?? '';
    this.loadReceivablesPayables();
  }

  ngAfterViewInit(): void {
    this.loadCashflowFromApi();
  }

  ngOnDestroy(): void {
    this.cashflowChartInstance?.destroy();
    this.ref?.close();
  }


  refreshAll(): void {
    this.loadReceivablesPayables();
    this.loadCashflowFromApi();
  }

  loadReceivablesPayables(): void {
    this.dashboardService.getAccountReceivableAndPayable()
      .subscribe({
        next: (data: { receivables: any; payables: any }) => {
          this.receivables = data.receivables;
          this.payables = data.payables;

          this.receivablePercent = this.receivables?.receivablePercent ?? 0;
          this.payablePercent = this.payables?.payablePercent ?? 0;

          this.computeNetPosition();
          setTimeout(() => this.runCountUps(), 120);
        },
        error: err => console.error('Receivables/Payables error:', err)
      });
  }

  private computeNetPosition(): void {
    const rec = this.receivables?.totalAmount ?? 0;
    const pay = this.payables?.totalAmount ?? 0;
    this.netPosition = rec - pay;
    const total = rec + pay;
    this.netPercent = total > 0 ? Math.round((rec / total) * 100) : 0;
  }

  runCountUps(): void {
    const opts = (prefix = '₹') => ({
      decimalPlaces: 0,
      separator: ',',
      duration: 4,
      useEasing: true,
      useGrouping: true,
      prefix: prefix,
      formattingFn: (num: number) => {
        return prefix + num.toLocaleString('en-IN', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        });
      }
    });

    const recVal = this.receivables?.totalAmount ?? 0;
    const payVal = this.payables?.totalAmount ?? 0;
    const netPos = this.netPosition ?? 0;
    const cashVal = this.closingCash ?? 0;

    new CountUp('acKpiReceivables', recVal, opts('₹')).start();
    new CountUp('acKpiPayables', payVal, opts('₹')).start();
    new CountUp('acKpiNetPosition', Math.abs(netPos), opts(netPos >= 0 ? '+ ₹' : '- ₹')).start();
    new CountUp('acKpiCash', cashVal, opts('₹')).start();
  }

  selectPeriod(value: string): void {
    this.selectedPeriod = value;
    this.loadCashflowFromApi();
  }

  loadCashflowFromApi(): void {
    const periodMap: Record<string, string> = {
      'This Fiscal Year': 'year',
      'Previous Fiscal Year': 'prev',
      'Last 12 Months': '12m'
    };
    const period = periodMap[this.selectedPeriod] ?? 'year';

    this.dashboardService.getCashflowChart(period).subscribe({
      next: (res: any) => {
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

        const incoming = new Array(12).fill(0);
        const outgoing = new Array(12).fill(0);

        (res.income ?? []).forEach((x: any) => { incoming[x.month - 1] = x.income ?? 0; });
        (res.expense ?? []).forEach((x: any) => { outgoing[x.month - 1] = x.expense ?? 0; });

        this.totalIncoming = incoming.reduce((a, b) => a + b, 0);
        this.totalOutgoing = outgoing.reduce((a, b) => a + b, 0);
        this.openingCash = res.openingCash ?? 0;
        this.closingCash = this.openingCash + this.totalIncoming - this.totalOutgoing;

        this.buildMonthlyTable(months, incoming, outgoing);
        this.renderCashflowChart(months, incoming, outgoing);
        setTimeout(() => this.runCountUps(), 120);
      },
      error: err => console.error('Cashflow error:', err)
    });
  }

  private buildMonthlyTable(months: string[], inc: number[], out: number[]): void {
    const maxVal = Math.max(...inc, ...out, 1);
    this.monthlyTable = months.map((m, i) => ({
      month: m,
      incoming: inc[i],
      outgoing: out[i],
      net: inc[i] - out[i],
      inPct: Math.round((inc[i] / maxVal) * 100),
      outPct: Math.round((out[i] / maxVal) * 100)
    }));
  }

  renderCashflowChart(labels: string[], incoming: number[], outgoing: number[]): void {
    this.cashflowChartInstance?.destroy();
    if (!this.cashflowCanvas?.nativeElement) return;

    const ctx = this.cashflowCanvas.nativeElement.getContext('2d');
    if (!ctx) return;

    this.cashflowChartInstance = new Chart(ctx, {
      type: 'line',
      data: {
        labels,
        datasets: [
          {
            label: 'Incoming ₹',
            data: incoming,
            borderColor: '#059669',
            backgroundColor: 'rgba(5,150,105,0.08)',
            fill: true,
            tension: 0.42,
            pointBackgroundColor: '#059669',
            pointBorderColor: '#ffffff',
            pointBorderWidth: 2,
            pointRadius: 5,
            pointHoverRadius: 7,
            borderWidth: 2.5
          },
          {
            label: 'Outgoing ₹',
            data: outgoing,
            borderColor: '#e11d48',
            backgroundColor: 'rgba(225,29,72,0.06)',
            fill: true,
            tension: 0.42,
            pointBackgroundColor: '#e11d48',
            pointBorderColor: '#ffffff',
            pointBorderWidth: 2,
            pointRadius: 5,
            pointHoverRadius: 7,
            borderWidth: 2.5
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        layout: { padding: { top: 10, right: 10 } },
        interaction: { mode: 'index', intersect: false },
        animation: { duration: 1000, easing: 'easeOutQuart' },
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: '#1a1748',
            titleColor: '#f0f0f8',
            bodyColor: '#9ba3c0',
            borderColor: '#e2e6f3',
            borderWidth: 1,
            padding: 12,
            callbacks: {
              label: (ctx: any) => {
                const v = ctx.raw as number;
                return `  ${ctx.dataset.label}: ₹${v.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
              }
            }
          }
        },
        scales: {
          x: {
            grid: { color: 'rgba(99,102,241,.06)' },
            border: { display: false },
            ticks: { color: '#9ba3c0', font: { size: 11, family: 'DM Sans' } }
          },
          y: {
            grid: { color: 'rgba(99,102,241,.06)' },
            border: { display: false },
            ticks: {
              color: '#6366f1',
              font: { size: 11, family: 'DM Sans' },
              callback: (v: any) => {
                if (v >= 100000) return '₹' + (v / 100000).toFixed(1) + 'L';
                if (v >= 1000) return '₹' + (v / 1000).toFixed(0) + 'K';
                return '₹' + v;
              }
            }
          }
        }
      }
    });
  }


  getIncomeRatioPct(): number {
    const total = this.totalIncoming + this.totalOutgoing;
    if (total === 0) return 50;
    return Math.round((this.totalIncoming / total) * 100);
  }

  formatINR(amount: number | null | undefined): string {
    if (amount == null) return '₹0';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  }


  toggleOverdue(event: Event): void {
    event.stopPropagation();
    this.showOverduePopup = !this.showOverduePopup;
    this.showPayablePopup = false;
  }

  togglePayableOverdue(event: Event): void {
    event.stopPropagation();
    this.showPayablePopup = !this.showPayablePopup;
    this.showOverduePopup = false;
  }

  @HostListener('document:click')
  closeAllPopups(): void {
    this.showOverduePopup = false;
    this.showPayablePopup = false;
  }


  openTopRecords(type: 'receivables' | 'payables'): void {
    if (this.isDialogOpen) return;
    this.isDialogOpen = true;

    this.ref = this.dialogService.open(TopAaccountingRecodsLookup, {
      header: 'Upcoming Dues',
      width: '70%',
      data: { type }
    });

    this.ref.onClose.subscribe(() => {
      this.isDialogOpen = false;
      this.ref = null;
    });
  }
}